import { CommonModule } from '@angular/common';
import { Component, inject, NgZone } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { AppService } from '../services/app.service';
import { UiSecretsService } from '../services/ui-secrets.service';
import { ToastrService } from 'ngx-toastr';
import { User } from '../model/user';
import { getStoredUser } from '../helpers/user-storage.util';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    pendo: any;
  }
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  isValid: boolean = true;
  isSubmitting = false;
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  emailRegx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  redirectUrl: string = '';
  showPassword: boolean = false;

  constructor(
    public router: Router,
    private authService: AuthenticationService,
    private appService: AppService,
    private uiSecretsService: UiSecretsService,
    private toastr: ToastrService,
    private ngZone: NgZone
  ) {
    this.appService.getMessage.subscribe((message) => {
      if (message != '') {
        this.toastr.success(message);
        this.appService.setMessage('');
      }
    });
    this.appService.redirectUrl.subscribe((url) => (this.redirectUrl = url));
  }

  ngOnInit() {}

  ngAfterViewInit() {
    let keepMeSigned = localStorage.getItem('keepMeSignedIn');
    if (keepMeSigned) {
      const user = getStoredUser();
      let password = localStorage.getItem('keepMeSignedInPassword');
      // getStoredUser() returns an empty object when nothing valid is cached, so
      // only proceed when there is an actual stored user (avoids patching an
      // undefined email and redirecting without a valid session).
      if (user.email) {
        this.loginForm.patchValue({
          email: user.email,
          password: password,
          keepMeSigned: 'true',
        });
        this.router.navigate(['invitations']);
      }
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  trimPassword() {
    let password = this.loginForm.get('password')?.value;
    if (password) {
      this.loginForm.patchValue({
        password: password.trim(),
      });
    }
  }

  protected loginForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(this.emailRegx),
    ]),
    password: new FormControl('', [Validators.required]),
    keepMeSigned: new FormControl(''),
  });

  onSubmit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
    }
    if (
      this.loginForm.value.password != '' &&
      this.loginForm.value.email != ''
    ) {
      if (this.loginForm.value.email?.match(this.emailRegx) === null) {
        this.isValid = false;
      }
    }
    if (
      this.loginForm.value.password != '' &&
      this.loginForm.value.password?.match(this.passwordRegx) === null
    ) {
      if (localStorage.getItem('user') != null) {
        // The forgot/reset/create-account flows store the wrapped API response
        // ({ data: User }) under the 'user' key; this path reads that shape.
        const user = (getStoredUser() as unknown as { data?: User }).data;
        if (user) {
          if (
            Math.floor(
              new Date(user.tempPasswordExpiration ?? '').getTime() -
                new Date().getTime()
            ) /
              1000 /
              60 <
            0
          ) {
            this.toastr.error('Your temporary password has expired.');
            return;
          }
        }
      }
    }
    if (this.loginForm.valid) {
      if (this.loginForm.value.email && this.loginForm.value.password) {
        this.isSubmitting = true;
        this.authService
          .login(this.loginForm.value.email, this.loginForm.value.password)
          .subscribe({
            next: (data) => {
              this.isSubmitting = false;
              if (
                this.loginForm.value.password != '' &&
                this.loginForm.value.password?.match(this.passwordRegx) != null
              ) {
                localStorage.setItem('user', JSON.stringify(data.data));
              }
              // might implement in the future
              // if (this.loginForm.value.keepMeSigned) {
              //   localStorage.setItem('keepMeSignedIn', 'true');
              //   localStorage.setItem('keepMeSignedInPassword', password);
              // } else {
              //   localStorage.removeItem('keepMeSignedIn');
              //   localStorage.removeItem('keepMeSignedInPassword');
              // }
              if (data.status === 'FAILURE') {
                this.toastr.error(data.message);
              }
              if (data.code === 302) {
                localStorage.setItem('email', this.loginForm.value.email ?? '');
                this.router.navigate(['resetpassword']);
              } else if (data.code === 200) {
                localStorage.setItem('token', data.token);
                sessionStorage.setItem('token', data.token);
                // Internal users have no map-based features; skip the load.
                if (data.data.role !== 'INTERNAL') {
                  this.uiSecretsService.loadGoogleMaps();
                }

                this.ngZone.runOutsideAngular(() => {
                  if (
                    window.pendo &&
                    typeof window.pendo.initialize === 'function'
                  ) {
                    const visitor = {
                      id: environment.env + data.data.id,
                      email: data.data.email,
                      full_name: data.data.firstName + ' ' + data.data.lastName,
                    };
                    window.pendo.initialize({
                      visitor: visitor,
                    });
                  }
                });

                if (data.data.role === 'INTERNAL') {
                  this.router.navigate(['internal']);
                } else if (data.data.newUser) {
                  this.router.navigate(['manageaccount'], {
                    queryParams: { tab: 'servicezones' },
                  });
                } else if (this.redirectUrl === '') {
                  this.router.navigate(['invitations']);
                } else {
                  this.router.navigate([this.redirectUrl]);
                }
              }
            },
            error: () => {
              this.isSubmitting = false;
              this.toastr.error('An error occurred. Please try again.');
            }
          });
      }
    }
  }

  navigateRegister() {
    this.router.navigate(['createaccount']);
  }

  clearValidations() {
    this.isValid = true;
  }
}
